import React, { useCallback, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { NotificationCenter } from '@/components/common/NotificationCenter';
import { ContextSwitcher } from '@/components/common/ContextSwitcher';
import { GlobalSearch } from '@/components/common/GlobalSearch';
import { useTheme } from '@/context/ThemeContext';
import useInternalCoverBlob from '@/components/calendar/useInternalCoverBlob';
import useCartCount from '@/hooks/useCartCount';
import { voiceAPI, urgentTasksAPI, agentApplicationAPI } from '@/lib/api';
import {
  isKRRole,
  isKRSuperAdmin,
  isAgencyPrincipal,
  isAgencyContext,
  isAgent,
  isAccountingRole,
  getInitials,
  getRoleDisplayName,
  getPrimaryRole,
} from '@/lib/utils';
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  Settings,
  Ticket,
  Workflow,
  Mail,
  FolderTree,
  ClipboardList,
  UserCircle,
  LogOut,
  Megaphone,
  UserCog,
  Zap,
  Sparkles,
  Shield,
  Moon,
  Sun,
  BarChart3,
  Activity,
  BellRing,
  DollarSign,
  CreditCard,
  Landmark,
  TrendingUp,
  Target,
  MessageSquare,
  Send,
  LayoutGrid,
  GraduationCap,
  Palette,
  CalendarClock,
  CalendarDays,
  Calculator,
  Key,
  Wrench,
  ShieldCheck,
  ChevronDown,
  Briefcase,
  ChevronUp,
  UserPlus,
  Gift,
  Award,
  FileStack,
  Upload,
  Package,
  History,
  BookOpen,
  Wallet,
  ShoppingBag,
  ShoppingCart,
  Bot,
  PhoneCall,
  Voicemail,
  AlertTriangle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const LOGO_DARK_BG = 'https://customer-assets-v7afamib.emergentagent.net/job_24da4b5c-f7b8-4285-a82c-9685a2a83611/artifacts/ej6yteqo_logo%20para%20fondo%20oscuro%20clear.webp';

// ── Nav primitives ─────────────────────────────────────────────────────────
const NavItem = ({ to, icon: Icon, children, onNavigate, testId }) => (
  <NavLink
    to={to}
    className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
    onClick={() => onNavigate?.()}
    data-testid={testId || `nav-${to.replace(/[/]/g, '-').replace(/^-/, '')}`}
  >
    <Icon size={18} />
    <span>{children}</span>
  </NavLink>
);

// Non-navigating placeholder for future modules (no route yet).
// Rendered as a disabled row with a "Soon" pill. Preserves IA intent.
const NavItemPlaceholder = ({ icon: Icon, children, testid }) => (
  <div
    className="sidebar-nav-item"
    data-testid={testid}
    aria-disabled="true"
    style={{ opacity: 0.55, cursor: 'not-allowed' }}
    title="Coming soon"
  >
    <Icon size={18} />
    <span style={{ flex: 1 }}>{children}</span>
    <span
      style={{
        fontSize: '10px',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: '9999px',
        border: '1px solid rgba(148, 163, 184, 0.35)',
        color: 'rgba(148, 163, 184, 0.9)',
      }}
    >
      Soon
    </span>
  </div>
);

// Marketing nav-item with a cart-count badge. Uses the same live cart
// count hook that used to drive the standalone Cart entry so users can
// still see at a glance when they have items pending checkout, even
// though the cart now lives inside `/marketing`.
const MarketingNavItem = ({ onNavigate }) => {
  const count = useCartCount();
  return (
    <NavLink
      to="/marketing"
      className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
      onClick={() => onNavigate?.()}
      data-testid="nav-marketing"
    >
      <Palette size={18} />
      <span style={{ flex: 1 }}>Marketing</span>
      {count > 0 && (
        <span
          data-testid="nav-marketing-cart-count"
          title={`${count} item${count === 1 ? '' : 's'} in your cart`}
          style={{
            minWidth: '20px',
            height: '20px',
            padding: '0 6px',
            borderRadius: '9999px',
            background: '#10b981',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          {count}
        </span>
      )}
    </NavLink>
  );
};


// Collapsible group. Persists open/closed state per (role|key) in localStorage.
// Voicemails nav-item with an unread-count badge (Phase 27.60). Polls the
// voicemail inbox so KR staff see new messages at a glance.
const VoicemailNavItem = ({ onNavigate }) => {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let stop = false;
    const poll = async () => {
      try { const r = await voiceAPI.voicemails(); if (!stop) setCount(r.data?.unread || 0); } catch (_) { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  return (
    <NavLink
      to="/voicemails"
      className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
      onClick={() => onNavigate?.()}
      data-testid="nav-voicemails"
    >
      <Voicemail size={18} />
      <span style={{ flex: 1 }}>Voicemails</span>
      {count > 0 && (
        <span
          data-testid="nav-voicemail-unread"
          title={`${count} new voicemail${count === 1 ? '' : 's'}`}
          style={{
            minWidth: '20px', height: '20px', padding: '0 6px', borderRadius: '9999px',
            background: '#f43f5e', color: '#fff', fontSize: '11px', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}
        >
          {count}
        </span>
      )}
    </NavLink>
  );
};

const ActionRequiredNavItem = ({ onNavigate }) => {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let stop = false;
    const poll = async () => {
      try { const r = await urgentTasksAPI.active(); if (!stop) setCount(r.data?.count || 0); } catch (_) { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  return (
    <NavLink
      to="/action-required"
      className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
      onClick={() => onNavigate?.()}
      data-testid="nav-action-required"
    >
      <AlertTriangle size={18} />
      <span style={{ flex: 1 }}>Action Required</span>
      {count > 0 && (
        <span
          data-testid="nav-action-required-count"
          title={`${count} item${count === 1 ? '' : 's'} require your action`}
          style={{
            minWidth: '20px', height: '20px', padding: '0 6px', borderRadius: '9999px',
            background: '#f43f5e', color: '#fff', fontSize: '11px', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}
        >
          {count}
        </span>
      )}
    </NavLink>
  );
};

// Agent Onboarding nav-item with a pending-applications badge. Polls the
// public-application review queue so KR staff see new submissions at a glance.
const OnboardingNavItem = ({ onNavigate }) => {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let stop = false;
    const poll = async () => {
      try { const r = await agentApplicationAPI.pendingCount(); if (!stop) setCount(r.data?.pending || 0); } catch (_) { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  return (
    <NavLink
      to="/onboarding-workflows"
      className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
      onClick={() => onNavigate?.()}
      data-testid="nav-onboarding-workflows"
    >
      <Workflow size={18} />
      <span style={{ flex: 1 }}>Agent Onboarding</span>
      {count > 0 && (
        <span
          data-testid="nav-onboarding-pending-count"
          title={`${count} application${count === 1 ? '' : 's'} awaiting review`}
          style={{
            minWidth: '20px', height: '20px', padding: '0 6px', borderRadius: '9999px',
            background: '#f43f5e', color: '#fff', fontSize: '11px', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}
        >
          {count}
        </span>
      )}
    </NavLink>
  );
};

const NavGroup = ({ id, label, defaultOpen = true, children }) => {
  const storageKey = `kr.nav.group.${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === null) return defaultOpen;
      return stored === '1';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem(storageKey, next ? '1' : '0');
      } catch { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  return (
    <div className="sidebar-group" data-testid={`nav-group-${id}`}>
      <button
        type="button"
        onClick={toggle}
        className={`sidebar-group-header ${open ? '' : 'collapsed'}`}
        aria-expanded={open}
        data-testid={`nav-group-toggle-${id}`}
      >
        <span>{label}</span>
        <ChevronDown className="chev" />
      </button>
      <div className={`sidebar-group-body ${open ? '' : 'collapsed'}`}>{children}</div>
    </div>
  );
};

/**
 * SidebarSubGroup — nested collapsible drawer used inside a NavGroup.
 * Same persistence + accessibility contract as NavGroup, but styled with
 * additional indent so it reads as a sub-item, not a top-level section.
 *
 * Keyboard: the header is a real <button> so Space/Enter toggle it. Focus
 * ring is inherited from the DS. Active-route highlighting on child links
 * is inherited from NavLink (unchanged).
 */
const SidebarSubGroup = ({ id, label, icon: Icon, defaultOpen = false, children }) => {
  const storageKey = `kr.nav.subgroup.${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === null) return defaultOpen;
      return stored === '1';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  return (
    <div className="sidebar-subgroup" data-testid={`nav-subgroup-${id}`}>
      <button
        type="button"
        onClick={toggle}
        className={`sidebar-nav-item sidebar-subgroup-header ${open ? 'open' : ''}`}
        aria-expanded={open}
        aria-controls={`nav-subgroup-body-${id}`}
        data-testid={`nav-subgroup-toggle-${id}`}
      >
        {Icon && <Icon size={18} />}
        <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
        <ChevronDown size={14} style={{ transition: 'transform .2s', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
      </button>
      <div
        id={`nav-subgroup-body-${id}`}
        className="sidebar-subgroup-body"
        style={{
          overflow: 'hidden',
          maxHeight: open ? '1000px' : '0',
          transition: 'max-height .25s ease',
          paddingLeft: '16px',
        }}
      >
        {children}
      </div>
    </div>
  );
};

// ── Role-specific nav trees ────────────────────────────────────────────────
// KR admin follows the Phase 19.2 restructured IA — 5 top-level groups plus
// a nested Administration sub-drawer for rarely-used config surfaces.
const KrNav = ({ isSuperAdmin, onNavigate }) => (
  <>
    <NavGroup id="kr-main" label="Main">
      <NavItem to="/dashboard" icon={LayoutDashboard} onNavigate={onNavigate}>Dashboard</NavItem>
    </NavGroup>

    <NavGroup id="kr-business" label="Business">
      <NavItem to="/agents" icon={Users} onNavigate={onNavigate}>Agents</NavItem>
      <NavItem to="/team" icon={UserCog} onNavigate={onNavigate} testId="nav-team">Team</NavItem>
      <NavItem to="/agencies" icon={FolderTree} onNavigate={onNavigate}>Agencies</NavItem>
      <NavItem to="/requests" icon={ClipboardList} onNavigate={onNavigate}>Contracts</NavItem>
      <NavItem to="/contracts/import-status" icon={Upload} onNavigate={onNavigate} testId="nav-contract-status-import">Import Contract Status</NavItem>
      <SidebarSubGroup id="kr-business-accounting" label="Accounting" icon={Calculator}>
        <NavItem to="/accounting/payment-center" icon={Wallet} onNavigate={onNavigate}>Payment Center</NavItem>
        <NavItem to="/accounting/carrier-imports" icon={Upload} onNavigate={onNavigate}>Payments</NavItem>
        <NavItem to="/accounting/bank-deposits" icon={Landmark} onNavigate={onNavigate}>Bank Deposits</NavItem>
        <NavItem to="/accounting/commission-schedules" icon={Calculator} onNavigate={onNavigate}>Commission Schedules</NavItem>
        <NavItem to="/accounting/unreceived-commission" icon={AlertTriangle} onNavigate={onNavigate}>Unreceived Commission</NavItem>
        <NavItem to="/accounting/statements" icon={FileText} onNavigate={onNavigate}>Statements</NavItem>
        <NavItem to="/accounting/payment-batches" icon={Package} onNavigate={onNavigate}>Payment Batches</NavItem>
        <NavItem to="/accounting/manual-adjustments" icon={ClipboardList} onNavigate={onNavigate}>Manual Adjustments</NavItem>
        <NavItem to="/accounting/reimbursements" icon={DollarSign} onNavigate={onNavigate}>Reimbursements</NavItem>
        <NavItem to="/accounting/ledger" icon={BookOpen} onNavigate={onNavigate}>Ledger Explorer</NavItem>
        <NavItem to="/accounting/history" icon={History} onNavigate={onNavigate}>History</NavItem>
      </SidebarSubGroup>
      <OnboardingNavItem onNavigate={onNavigate} />
    </NavGroup>

    <NavGroup id="kr-contracting" label="Contracting">
      <NavItem to="/kanban" icon={LayoutGrid} onNavigate={onNavigate}>Contracting Pipeline</NavItem>
      <NavItem to="/catalog" icon={Briefcase} onNavigate={onNavigate}>Carrier Management</NavItem>
      <NavItem to="/level-caps" icon={Building2} onNavigate={onNavigate}>Level Caps</NavItem>
      <NavItem to="/release-followups" icon={CalendarClock} onNavigate={onNavigate}>Appointments</NavItem>
    </NavGroup>

    <NavGroup id="kr-operations" label="Operations">
      <NavItem to="/tickets" icon={Ticket} onNavigate={onNavigate}>Tickets</NavItem>
      <NavItem to="/urgent-tasks" icon={AlertTriangle} onNavigate={onNavigate} testId="nav-urgent-tasks">Urgent Tasks</NavItem>
      <MarketingNavItem onNavigate={onNavigate} />
      <NavItem to="/knowledge" icon={GraduationCap} onNavigate={onNavigate} testId="nav-knowledge">Knowledge</NavItem>
      <NavItem to="/academy" icon={BookOpen} onNavigate={onNavigate} testId="nav-academy">Academy</NavItem>
      <NavItem to="/trainees" icon={GraduationCap} onNavigate={onNavigate} testId="nav-pre-licensing">Pre-Licensing</NavItem>
      <NavItem to="/prospects" icon={UserPlus} onNavigate={onNavigate}>Prospects</NavItem>
      <NavItem to="/referrals" icon={Gift} onNavigate={onNavigate} testId="nav-referrals">Referrals</NavItem>
      <NavItem to="/leads" icon={Target} onNavigate={onNavigate}>Leads</NavItem>
      <NavItem to="/clients" icon={Users} onNavigate={onNavigate} testId="nav-clients">Clients</NavItem>
      <NavItem to="/kora/analytics" icon={Sparkles} onNavigate={onNavigate} testId="nav-kora-analytics">Kora Analytics</NavItem>
      <NavItem to="/kora/training" icon={Bot} onNavigate={onNavigate} testId="nav-kora-training">Kora Training</NavItem>
      <NavItem to="/sms-inbox" icon={MessageSquare} onNavigate={onNavigate} testId="nav-sms-inbox">SMS Inbox</NavItem>
      <NavItem to="/sms-broadcasts" icon={Send} onNavigate={onNavigate} testId="nav-sms-broadcasts">SMS Broadcasts</NavItem>
      <NavItem to="/settings/voice" icon={PhoneCall} onNavigate={onNavigate} testId="nav-voice-calling">Calling</NavItem>
      <NavItem to="/call-history" icon={History} onNavigate={onNavigate} testId="nav-call-history">Call History</NavItem>
      <NavItem to="/settings/voice/supervision" icon={PhoneCall} onNavigate={onNavigate} testId="nav-voice-supervision">Live call center</NavItem>
      <NavItem to="/settings/voice/usage" icon={PhoneCall} onNavigate={onNavigate} testId="nav-voice-usage">Calling usage</NavItem>
      <VoicemailNavItem onNavigate={onNavigate} />
      <NavItem to="/communication-log" icon={MessageSquare} onNavigate={onNavigate} testId="nav-comm-log">Communication Log</NavItem>
      <NavItem to="/reports" icon={BarChart3} onNavigate={onNavigate} testId="nav-reports">Reports</NavItem>
      <NavItem to="/supplies" icon={Package} onNavigate={onNavigate} testId="nav-supplies">Event Equipment</NavItem>
    </NavGroup>

    <NavGroup id="kr-events" label="Events & Calendar">
      <NavItem to="/events-calendar/dashboard" icon={LayoutGrid} onNavigate={onNavigate}>Dashboard</NavItem>
      <NavItem to="/calendar" icon={CalendarDays} onNavigate={onNavigate}>Calendar</NavItem>
      <NavItem to="/recognition" icon={Award} onNavigate={onNavigate}>Recognition</NavItem>
      <NavItem to="/portal/events" icon={Ticket} onNavigate={onNavigate}>Events</NavItem>
    </NavGroup>

    <NavGroup id="kr-admin" label="Administration" defaultOpen={false}>
      <NavItem to="/platform-settings" icon={Settings} onNavigate={onNavigate}>Settings</NavItem>
      <NavItem to="/audit-log" icon={Shield} onNavigate={onNavigate}>Audit Logs</NavItem>
      <NavItem to="/admin/contract-duplicates" icon={FileStack} onNavigate={onNavigate}>Duplicate Contracts</NavItem>
      <NavItem to="/users" icon={UserCog} onNavigate={onNavigate}>Permissions</NavItem>
      <NavItem to="/notification-settings" icon={BellRing} onNavigate={onNavigate}>Notifications</NavItem>
      <NavItem to="/settings/auto-reminders" icon={Zap} onNavigate={onNavigate} testId="nav-auto-reminders">Lead Communication</NavItem>
      <NavItem to="/settings/integrations/twilio" icon={MessageSquare} onNavigate={onNavigate} testId="nav-twilio-integration">Twilio Integration</NavItem>
      <NavItem to="/settings/integrations/quickbooks" icon={CreditCard} onNavigate={onNavigate} testId="nav-quickbooks-integration">QuickBooks Integration</NavItem>
      <NavItem to="/admin/storefront" icon={ShoppingBag} onNavigate={onNavigate} testId="nav-admin-storefront">Storefront Manager</NavItem>
      <NavItem to="/settings/seasonal-themes" icon={Palette} onNavigate={onNavigate} testId="nav-seasonal-themes">Seasonal Themes</NavItem>
      <NavItem to="/admin/deletion-log" icon={History} onNavigate={onNavigate} testId="nav-deletion-log">Deletion Log</NavItem>
      {isSuperAdmin && (
        <NavItem to="/admin/disclaimer" icon={ShieldCheck} onNavigate={onNavigate} testId="nav-admin-disclaimer">Compliance · Disclaimer</NavItem>
      )}
      <SidebarSubGroup id="kr-admin-system-tools" label="System Tools" icon={Wrench}>
        {/* Phase 27.38 — Delisted: Workflow Settings (dead — only 1 unused
            knob), Email Templates (dead — CRUD without any consumer),
            Compensation (KR admin never uses it; still linked from the
            Accounting-role nav), Orphan Carriers (cleanup task completed),
            Level Caps (moved up to the Contracting group next to Carrier
            Management for discoverability). */}
        <NavItem to="/announcements" icon={Megaphone} onNavigate={onNavigate}>Announcements</NavItem>
        <NavItem to="/invitations" icon={Mail} onNavigate={onNavigate}>Invitations</NavItem>
        <NavItem to="/managers" icon={UserCog} onNavigate={onNavigate}>Managers</NavItem>
        <NavItem to="/accounting/eft" icon={Landmark} onNavigate={onNavigate}>Payment Info (EFT)</NavItem>
        <NavItem to="/admin/aep-certifications" icon={ShieldCheck} onNavigate={onNavigate}>AEP Certifications</NavItem>
        {isSuperAdmin && (
          <NavItem to="/admin/jarvis-keys" icon={Key} onNavigate={onNavigate}>JARVIS API Keys</NavItem>
        )}
      </SidebarSubGroup>
    </NavGroup>
  </>
);

// Non-KR navs follow the same 3-4 top-level group discipline as KR (Phase
// 19.2). Profile/Account Settings/Logout live in the user menu dropdown, not
// the sidebar. Deprecated financial/production/lead surfaces are intentionally
// excluded from Agent nav.

const AccountingNav = ({ onNavigate }) => (
  <>
    <NavGroup id="acc-main" label="Main">
      <NavItem to="/dashboard" icon={LayoutDashboard} onNavigate={onNavigate}>Dashboard</NavItem>
    </NavGroup>
    <NavGroup id="acc-financial" label="Financial">
      <NavItem to="/accounting/compensation" icon={Calculator} onNavigate={onNavigate}>Compensation Center</NavItem>
      <NavItem to="/accounting/eft" icon={Landmark} onNavigate={onNavigate}>Payment Info (EFT)</NavItem>
    </NavGroup>
    {/* Phase 25.1 — Accounting module (Mapping is inside the Carrier Import workflow, not a top-level nav) */}
    <NavGroup id="acc-module" label="Accounting">
      <NavItem to="/accounting/payment-center" icon={Wallet} onNavigate={onNavigate}>Payment Center</NavItem>
      <NavItem to="/accounting/carrier-imports" icon={Upload} onNavigate={onNavigate}>Payments</NavItem>
      <NavItem to="/accounting/bank-deposits" icon={Landmark} onNavigate={onNavigate}>Bank Deposits</NavItem>
      <NavItem to="/accounting/commission-schedules" icon={Calculator} onNavigate={onNavigate}>Commission Schedules</NavItem>
      <NavItem to="/accounting/unreceived-commission" icon={AlertTriangle} onNavigate={onNavigate}>Unreceived Commission</NavItem>
      <NavItem to="/accounting/statements" icon={FileText} onNavigate={onNavigate}>Statements</NavItem>
      <NavItem to="/accounting/payment-batches" icon={Package} onNavigate={onNavigate}>Payment Batches</NavItem>
      <NavItem to="/accounting/manual-adjustments" icon={ClipboardList} onNavigate={onNavigate}>Manual Adjustments</NavItem>
      <NavItem to="/accounting/reimbursements" icon={DollarSign} onNavigate={onNavigate}>Reimbursements</NavItem>
      <NavItem to="/accounting/ledger" icon={BookOpen} onNavigate={onNavigate}>Ledger Explorer</NavItem>
      <NavItem to="/accounting/history" icon={History} onNavigate={onNavigate}>History</NavItem>
    </NavGroup>
    <NavGroup id="acc-events" label="Events & Calendar">
      <NavItem to="/events-calendar/dashboard" icon={LayoutGrid} onNavigate={onNavigate}>Dashboard</NavItem>
      <NavItem to="/calendar" icon={CalendarDays} onNavigate={onNavigate}>Calendar</NavItem>
      <NavItem to="/recognition" icon={Award} onNavigate={onNavigate}>Recognition</NavItem>
      <NavItem to="/portal/events" icon={Ticket} onNavigate={onNavigate}>Events</NavItem>
    </NavGroup>
  </>
);

const AgencyNav = ({ isPrincipal, onNavigate }) => (
  <>
    <NavGroup id="ag-main" label="Main">
      <NavItem to="/dashboard" icon={LayoutDashboard} onNavigate={onNavigate}>Dashboard</NavItem>
    </NavGroup>
    <NavGroup id="ag-management" label="Agency Management">
      <NavItem to="/agencies" icon={FolderTree} onNavigate={onNavigate}>My Agency</NavItem>
      <NavItem to="/agents" icon={Users} onNavigate={onNavigate}>Agents</NavItem>
      <NavItem to="/team" icon={UserCog} onNavigate={onNavigate}>Team</NavItem>
      <NavItem to="/requests" icon={ClipboardList} onNavigate={onNavigate}>Contracts</NavItem>
      {isPrincipal && (
        <NavItem to="/urgent-tasks" icon={AlertTriangle} onNavigate={onNavigate} testId="nav-urgent-tasks-agency">Urgent Tasks</NavItem>
      )}
      {isPrincipal && (
        <NavItem to="/request-contract" icon={FileText} onNavigate={onNavigate}>New Request</NavItem>
      )}
    </NavGroup>
    <NavGroup id="ag-events" label="Events & Calendar">
      <NavItem to="/events-calendar/dashboard" icon={LayoutGrid} onNavigate={onNavigate}>Dashboard</NavItem>
      <NavItem to="/calendar" icon={CalendarDays} onNavigate={onNavigate}>Calendar</NavItem>
      <NavItem to="/recognition" icon={Award} onNavigate={onNavigate}>Recognition</NavItem>
      <NavItem to="/portal/events" icon={Ticket} onNavigate={onNavigate}>Events</NavItem>
    </NavGroup>
    <NavGroup id="ag-resources" label="Resources">
      <NavItem to="/leads" icon={Target} onNavigate={onNavigate}>Leads</NavItem>
      <NavItem to="/clients" icon={Users} onNavigate={onNavigate} testId="nav-clients-agency">Clients</NavItem>
      <NavItem to="/reimbursements" icon={DollarSign} onNavigate={onNavigate} testId="nav-reimbursements-agency">Reimbursements</NavItem>
      <MarketingNavItem onNavigate={onNavigate} />
      <NavItem to="/knowledge" icon={GraduationCap} onNavigate={onNavigate}>Knowledge</NavItem>
      <NavItem to="/academy" icon={BookOpen} onNavigate={onNavigate} testId="nav-academy">Academy</NavItem>
      <NavItem to="/tickets" icon={Ticket} onNavigate={onNavigate}>Support</NavItem>
      <NavItem to="/notification-settings" icon={BellRing} onNavigate={onNavigate} testId="nav-agency-notifications">Notifications & SMS</NavItem>
    </NavGroup>
  </>
);

const AgentNav = ({ onNavigate }) => (
  <>
    <NavGroup id="me-main" label="Main">
      <NavItem to="/dashboard" icon={LayoutDashboard} onNavigate={onNavigate}>Dashboard</NavItem>
      <ActionRequiredNavItem onNavigate={onNavigate} />
    </NavGroup>
    <NavGroup id="me-business" label="My Business">
      {/* Phase 27.37.Y — Consolidated. Removed duplicate "My Contracts"
          link; `/contracts` is the single source of truth showing all
          contract requests + full detail on click. */}
      <NavItem to="/contracts" icon={FileText} onNavigate={onNavigate}>Contracts</NavItem>
      <NavItem to="/leads" icon={Target} onNavigate={onNavigate}>My Leads</NavItem>
      <NavItem to="/clients" icon={Users} onNavigate={onNavigate} testId="nav-clients-agent">My Clients</NavItem>
      <NavItem to="/reimbursements" icon={DollarSign} onNavigate={onNavigate} testId="nav-reimbursements-agent">Reimbursements</NavItem>
      <NavItem to="/settings/voice" icon={PhoneCall} onNavigate={onNavigate} testId="nav-agent-call-history">Call History</NavItem>
      <NavItem to="/my-landing-page" icon={Megaphone} onNavigate={onNavigate} testId="nav-my-landing-page">My Landing Page</NavItem>
    </NavGroup>
    <NavGroup id="me-events" label="Events & Calendar">
      <NavItem to="/events-calendar/dashboard" icon={LayoutGrid} onNavigate={onNavigate}>Dashboard</NavItem>
      <NavItem to="/calendar" icon={CalendarDays} onNavigate={onNavigate}>Calendar</NavItem>
      <NavItem to="/recognition" icon={Award} onNavigate={onNavigate}>Recognition</NavItem>
    </NavGroup>
    <NavGroup id="me-resources" label="Resources">
      <MarketingNavItem onNavigate={onNavigate} />
      <NavItem to="/knowledge" icon={GraduationCap} onNavigate={onNavigate}>Knowledge</NavItem>
      <NavItem to="/academy" icon={BookOpen} onNavigate={onNavigate} testId="nav-academy">Academy</NavItem>
      <NavItem to="/referrals" icon={Gift} onNavigate={onNavigate} testId="nav-agent-referrals">Refer an Agent</NavItem>
      <NavItem to="/tickets" icon={Ticket} onNavigate={onNavigate}>Support</NavItem>
      <NavItem to="/notification-settings" icon={BellRing} onNavigate={onNavigate} testId="nav-agent-notifications">Notifications & SMS</NavItem>
    </NavGroup>
  </>
);

// ── Root ───────────────────────────────────────────────────────────────────
// Phase 27.38 — Sidebar avatar that shows the current user's agent photo
// (fetched via the auth-safe blob hook) and falls back to initials when
// no photo is on file. Non-agent users always see initials.
const SidebarUserAvatar = ({ user }) => {
  const photoUrl = user?.photo_url || null;
  const blobUrl = useInternalCoverBlob(photoUrl);
  const initials = getInitials(user?.name);
  if (blobUrl) {
    return (
      <div
        className="sidebar-user-avatar flex-shrink-0 overflow-hidden"
        style={{ backgroundImage: 'none', padding: 0 }}
        data-testid="sidebar-user-avatar-photo"
      >
        <img
          src={blobUrl}
          alt={user?.name || 'Avatar'}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  return (
    <div className="sidebar-user-avatar flex-shrink-0" data-testid="sidebar-user-avatar-initials">
      {initials}
    </div>
  );
};

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isKR = isKRRole(user);
  const isSuperAdmin = isKRSuperAdmin(user);
  const isPrincipal = isAgencyPrincipal(user);
  const isAgency = isAgencyContext(user);
  const isAgentUser = isAgent(user);
  const isAccounting = isAccountingRole(user);

  const handleLogout = () => {
    logout();
    onClose?.();
    navigate('/login');
  };
  const onNavigate = () => onClose?.();

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`} data-testid="sidebar">
      <div className="sidebar-logo">
        <img src={LOGO_DARK_BG} alt="KR Agency Hub" />
      </div>

      <div className="px-3 py-2">
        <GlobalSearch />
      </div>

      <nav className="sidebar-nav sidebar-scroll">
        {isKR && <KrNav isSuperAdmin={isSuperAdmin} onNavigate={onNavigate} />}
        {isAccounting && !isKR && <AccountingNav onNavigate={onNavigate} />}
        {isAgency && !isKR && !isAccounting && (
          <AgencyNav isPrincipal={isPrincipal} onNavigate={onNavigate} />
        )}
        {isAgentUser && !isKR && !isAgency && !isAccounting && (
          <AgentNav onNavigate={onNavigate} />
        )}
      </nav>

      <div className="sidebar-user">
        <ContextSwitcher />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="w-full flex items-center gap-2 mt-2 p-1 rounded-lg hover:bg-white/10 transition-colors text-left"
              data-testid="user-menu-trigger"
              aria-label="Open user menu"
            >
              <SidebarUserAvatar user={user} />
              <div className="sidebar-user-info min-w-0 flex-1">
                <div className="sidebar-user-name">{user?.name}</div>
                <div className="sidebar-user-role">{getRoleDisplayName(getPrimaryRole(user))}</div>
              </div>
              <ChevronUp size={14} className="text-slate-400 flex-shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            className="w-56"
            data-testid="user-menu-content"
          >
            <DropdownMenuLabel className="truncate">
              {user?.email || user?.name}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                onClose?.();
                // Phase 27.50.B.21 — "My Profile" always opens /profile
                // (the personal profile with Google Calendar, email
                // preferences, etc.). Previously agents were sent to
                // /agents/{npn} which is the admin view of their record.
                navigate('/profile');
              }}
              data-testid="user-menu-profile"
            >
              <UserCircle size={16} className="mr-2" />
              My Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                onClose?.();
                // Route to the settings hub — currently that's the
                // notification & timezone preferences page. When more
                // settings surfaces land (password, sessions, etc.)
                // this becomes the /settings index.
                navigate('/notification-settings');
              }}
              data-testid="user-menu-account-settings"
            >
              <Settings size={16} className="mr-2" />
              Account Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={handleLogout}
              data-testid="user-menu-logout"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex items-center gap-1 mt-2 justify-end">
          {/* Phase 27.30.25 — Theme toggle removed. App is light-only. */}
          <NotificationCenter />
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
            data-testid="logout-btn"
          >
            <LogOut size={16} className="text-slate-400" />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
